import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n";
import { usePageMeta } from "@/lib/format";
import { Input } from "@/components/ui/input";
import { ChevronLeft, Gift, CheckCircle2 } from "lucide-react";

export default function RedeemPledge() {
  const { t } = useI18n();
  const navigate = useNavigate();
  usePageMeta(t("pledge_code_einlo_sen"));
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");

  const redeem = async () => {
    const c = code.trim().toUpperCase();
    if (!c || busy) return;
    setBusy(true); setErr("");
    try { await api.post("/pledges/redeem", { code: c }); setDone(true); }
    catch (e) {
      const status = e?.response?.status;
      const detail = String(e?.response?.data?.detail || "").toLowerCase();
      if (status === 402 || detail.includes("setup") || detail.includes("payment")) setErr(t("redeem_needs_payment"));
      else setErr(t("redeem_invalid"));
    } finally { setBusy(false); }
  };

  if (done) {
    return (
      <div className="mx-auto flex max-w-md flex-col items-center gap-4 py-16 text-center" data-testid="redeem-done">
        <span className="flex h-20 w-20 items-center justify-center rounded-full bg-primary/15"><CheckCircle2 className="h-10 w-10 text-primary" /></span>
        <h1 className="font-display text-2xl text-foreground">{t("willkommen_bei_my_within")}</h1>
        <p className="text-sm text-muted-foreground">{t("dein_vorteil_wurde_deinem_account")}</p>
        <button onClick={() => navigate("/app")} data-testid="redeem-home" className="mt-2 rounded-full bg-primary px-6 py-2.5 font-semibold text-primary-foreground">{t("zuru_ck_zur_startseite")}</button>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-md space-y-5" data-testid="redeem-screen">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate("/app/account")} data-testid="redeem-back" className="flex h-9 w-9 items-center justify-center rounded-full bg-secondary/60 hover:bg-accent"><ChevronLeft className="h-5 w-5" /></button>
        <h1 className="font-display text-2xl text-foreground">{t("pledge_code_einlo_sen")}</h1>
      </div>

      <div className="flex flex-col items-center gap-3 rounded-2xl border border-border bg-card p-6 text-center">
        <span className="flex h-14 w-14 items-center justify-center rounded-full bg-primary/15"><Gift className="h-7 w-7 text-primary" /></span>
        <p className="text-sm text-muted-foreground">{t("hast_du_unsere_crowdfunding_kampagne")}</p>
      </div>

      <div className="space-y-2">
        <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t("dein_code")}</label>
        <Input value={code} onChange={(e) => { setCode(e.target.value); setErr(""); }} placeholder={t("mw_xxx_xxxx")} className="text-center font-mono text-lg uppercase tracking-widest" data-testid="redeem-input" onKeyDown={(e) => e.key === "Enter" && redeem()} />
        <p className="text-[11px] text-muted-foreground">{t("case_insensitive_format_mw_gefolgt_von")}</p>
        {err ? <p className="text-sm font-medium text-destructive" data-testid="redeem-error">{err}</p> : null}
      </div>

      <button onClick={redeem} disabled={!code.trim() || busy} data-testid="redeem-submit" className="w-full rounded-full bg-primary py-3 font-semibold text-primary-foreground disabled:opacity-50">{t("weiter_zur_zahlungsverifizierung")}</button>
      <p className="text-center text-xs text-muted-foreground">{t("kein_code_erhalten")} · {t("support_my_within_com")}</p>
    </div>
  );
}
